/**
 * Pick Your Favorites
 * Candy Information Window
 *
 * Responsibilities:
 * - Open and close the shared candy information window
 * - Load the selected candy's information
 * - Close with the X button
 * - Close by clicking the background overlay
 * - Close with the Escape key
 * - Keep keyboard focus inside the window
 * - Support Shopify Theme Editor section reloads
 * - Read popup wording from Shopify Theme Editor settings
 * - Add candy from the popup using existing product-card logic
 */

window.PYF = window.PYF || {};


PYF.infoModal = {
  modal: null,
  dialog: null,
  activeCard: null,
  previousFocus: null,
  initialized: false
};


/**
 * Read popup wording selected in the
 * Shopify Theme Editor.
 */
PYF.getInfoModalWording = function () {
  const settings =
    document.getElementById("pyf-info-settings");

  return {
    addButton:
      settings?.dataset.addButton ||
      "Add to Bag",

    outOfStock:
      settings?.dataset.outOfStock ||
      "Out of Stock",

    noDescription:
      settings?.dataset.noDescription ||
      "No description is currently available for this candy.",

    noIngredients:
      settings?.dataset.noIngredients ||
      "Ingredient information is not currently available.",

    noAllergens:
      settings?.dataset.noAllergens ||
      "Allergen information is not currently available.",

    noDietary:
      settings?.dataset.noDietary ||
      "Dietary information is not currently available."
  };
};


/**
 * Initialize the candy information window.
 */
PYF.initInfoModal = function () {
  const modal =
    document.getElementById("pyf-info-modal");

  if (!modal) return;


  const dialog =
    modal.querySelector(".pyf-info-modal__dialog");

  if (!dialog) return;


  PYF.infoModal.modal = modal;
  PYF.infoModal.dialog = dialog;


  /*
   * Prevent duplicate listeners when Shopify reloads
   * the section inside the Theme Editor.
   */
  if (
    modal.dataset.pyfInfoInitialized !== "true"
  ) {
    modal.dataset.pyfInfoInitialized = "true";


    /*
     * Close using:
     * - X button
     * - Background overlay
     *
     * closest() ensures clicking the icon inside the
     * close button still closes the popup.
     */
    modal.addEventListener(
      "click",
      function (event) {
        const closeTrigger =
          event.target.closest(
            "[data-pyf-info-close]"
          );

        if (!closeTrigger) return;


        event.preventDefault();

        PYF.closeInfoModal();
      }
    );


    /*
     * Escape key and focus trap.
     */
    document.addEventListener(
      "keydown",
      PYF.handleInfoModalKeydown
    );
  }


  /*
   * Event delegation allows candy information buttons
   * to keep working after Shopify reloads the section.
   */
  if (
    document.documentElement.dataset
      .pyfInfoButtonInitialized !== "true"
  ) {
    document.documentElement.dataset
      .pyfInfoButtonInitialized = "true";


    document.addEventListener(
      "click",
      function (event) {
        const infoButton =
          event.target.closest(
            "[data-pyf-info-button]"
          );

        if (!infoButton) return;


        event.preventDefault();
        event.stopPropagation();

        PYF.openInfoModal(infoButton);
      }
    );
  }


  PYF.infoModal.initialized = true;
};


/**
 * Open the information window and load
 * the selected candy.
 */
PYF.openInfoModal = function (triggerButton) {
  const modal = PYF.infoModal.modal;

  if (!modal || !triggerButton) return;


  const candyCard =
    triggerButton.closest(".pyf-candy-card");

  if (!candyCard) return;


  PYF.infoModal.activeCard = candyCard;


  PYF.infoModal.previousFocus =
    triggerButton instanceof HTMLElement
      ? triggerButton
      : document.activeElement;


  PYF.populateInfoModal(candyCard);


  modal.hidden = false;

  modal.setAttribute(
    "aria-hidden",
    "false"
  );


  document.body.classList.add(
    "pyf-info-modal-open"
  );


  window.requestAnimationFrame(
    function () {
      const closeButton =
        modal.querySelector(
          ".pyf-info-modal__close"
        );

      if (closeButton) {
        closeButton.focus();
      }
    }
  );
};


/**
 * Load the selected candy's information
 * into the popup.
 */
PYF.populateInfoModal = function (candyCard) {
  const modal = PYF.infoModal.modal;

  if (!modal || !candyCard) return;


  const wording =
    PYF.getInfoModalWording();


  const titleElement =
    modal.querySelector(
      "#pyf-info-title"
    );


  const priceElement =
    modal.querySelector(
      "#pyf-info-price"
    );


  const imageElement =
    modal.querySelector(
      "#pyf-info-image"
    );


  const imagePlaceholder =
    modal.querySelector(
      "#pyf-info-image-placeholder"
    );


  const descriptionElement =
    modal.querySelector(
      "#pyf-info-description"
    );


  const ingredientsElement =
    modal.querySelector(
      "#pyf-info-ingredients"
    );


  const allergensElement =
    modal.querySelector(
      "#pyf-info-allergens"
    );


  const dietaryElement =
    modal.querySelector(
      "#pyf-info-dietary"
    );


  const addButton =
    modal.querySelector(
      "#pyf-info-add-button"
    );


  const productInfo =
    candyCard.querySelector(
      "[data-pyf-product-info]"
    );


  const productTitle =
    candyCard.dataset.productTitle ||
    "Candy Information";


  const productImage =
    candyCard.dataset.image || "";


  const variantAvailable =
    candyCard.dataset.variantAvailable ===
    "true";


  const descriptionSource =
    productInfo?.querySelector(
      "[data-pyf-product-description]"
    );


  const ingredientsSource =
    productInfo?.querySelector(
      "[data-pyf-product-ingredients]"
    );


  const allergensSource =
    productInfo?.querySelector(
      "[data-pyf-product-allergens]"
    );


  const dietarySource =
    productInfo?.querySelector(
      "[data-pyf-product-dietary]"
    );


  /*
   * Close all accordions whenever a new
   * candy is opened.
   */
  modal
    .querySelectorAll(
      ".pyf-info-modal__accordion"
    )
    .forEach(function (accordion) {
      accordion.removeAttribute("open");
    });


  /*
   * Title and price.
   */
  if (titleElement) {
    titleElement.textContent =
      productTitle;
  }


  if (priceElement) {
    priceElement.textContent =
      "$1.89 per Pick";
  }


  /*
   * Product image.
   */
  if (
    imageElement &&
    imagePlaceholder
  ) {
    if (productImage) {
      imageElement.src =
        productImage;

      imageElement.alt =
        productTitle;

      imageElement.hidden =
        false;

      imagePlaceholder.hidden =
        true;
    } else {
      imageElement.removeAttribute(
        "src"
      );

      imageElement.alt = "";

      imageElement.hidden =
        true;

      imagePlaceholder.hidden =
        false;
    }
  }


  /*
   * Description.
   */
  PYF.copyInfoContent(
    descriptionElement,
    descriptionSource,
    `<p>${PYF.escapeInfoModalText(
      wording.noDescription
    )}</p>`
  );


  /*
   * Ingredients.
   */
  PYF.copyInfoContent(
    ingredientsElement,
    ingredientsSource,
    PYF.escapeInfoModalText(
      wording.noIngredients
    )
  );


  /*
   * Allergens.
   */
  PYF.copyInfoContent(
    allergensElement,
    allergensSource,
    PYF.escapeInfoModalText(
      wording.noAllergens
    )
  );


  /*
   * Good to Know.
   */
  PYF.copyInfoContent(
    dietaryElement,
    dietarySource,
    PYF.escapeInfoModalText(
      wording.noDietary
    )
  );


  /*
   * Prepare the popup Add to Bag button.
   */
  if (addButton) {
    addButton.dataset.productId =
      candyCard.dataset.productId || "";

    addButton.dataset.variantId =
      candyCard.dataset.variantId || "";


    if (variantAvailable) {
      addButton.textContent =
        wording.addButton;

      addButton.disabled =
        false;

      addButton.setAttribute(
        "aria-disabled",
        "false"
      );
    } else {
      addButton.textContent =
        wording.outOfStock;

      addButton.disabled =
        true;

      addButton.setAttribute(
        "aria-disabled",
        "true"
      );
    }
  }
};


/**
 * Escape Theme Editor fallback wording before
 * inserting it into popup HTML.
 */
PYF.escapeInfoModalText = function (value) {
  const temporaryElement =
    document.createElement("div");

  temporaryElement.textContent =
    value || "";

  return temporaryElement.innerHTML;
};


/**
 * Copy hidden product content into
 * a popup section.
 */
PYF.copyInfoContent = function (
  destination,
  source,
  fallbackContent
) {
  if (!destination) return;


  if (
    source &&
    source.innerHTML &&
    source.innerHTML.trim() !== ""
  ) {
    destination.innerHTML =
      source.innerHTML.trim();
  } else {
    destination.innerHTML =
      fallbackContent;
  }
};


/**
 * Close the candy information window.
 */
PYF.closeInfoModal = function () {
  const modal = PYF.infoModal.modal;

  if (!modal || modal.hidden) return;


  modal.hidden = true;

  modal.setAttribute(
    "aria-hidden",
    "true"
  );


  document.body.classList.remove(
    "pyf-info-modal-open"
  );


  if (
    PYF.infoModal.previousFocus &&
    typeof PYF.infoModal.previousFocus.focus ===
      "function"
  ) {
    PYF.infoModal.previousFocus.focus();
  }


  PYF.infoModal.previousFocus =
    null;

  PYF.infoModal.activeCard =
    null;
};


/**
 * Handle Escape and keep keyboard focus
 * inside the popup.
 */
PYF.handleInfoModalKeydown = function (event) {
  const modal = PYF.infoModal.modal;

  if (!modal || modal.hidden) return;


  if (event.key === "Escape") {
    event.preventDefault();

    PYF.closeInfoModal();

    return;
  }


  if (event.key !== "Tab") return;


  const focusableElements =
    modal.querySelectorAll(
      [
        "button:not([disabled])",
        "a[href]",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        "summary",
        '[tabindex]:not([tabindex="-1"])'
      ].join(",")
    );


  if (!focusableElements.length) {
    event.preventDefault();

    return;
  }


  const firstElement =
    focusableElements[0];


  const lastElement =
    focusableElements[
      focusableElements.length - 1
    ];


  if (
    event.shiftKey &&
    document.activeElement ===
      firstElement
  ) {
    event.preventDefault();

    lastElement.focus();
  } else if (
    !event.shiftKey &&
    document.activeElement ===
      lastElement
  ) {
    event.preventDefault();

    firstElement.focus();
  }
};


/* =====================================================
   POPUP ADD TO BAG
===================================================== */


/**
 * Add the active popup candy using the same product-card
 * logic that already handles inventory and bag capacity.
 */
PYF.addProductFromInfoModal = function () {
  const modal = PYF.infoModal.modal;

  const activeCard =
    PYF.infoModal.activeCard;

  if (
    !modal ||
    !activeCard ||
    typeof PYF.addProductFromCard !== "function"
  ) {
    return;
  }


  const product =
    typeof PYF.getProductFromCard === "function"
      ? PYF.getProductFromCard(activeCard)
      : null;


  const quantityBefore =
    product &&
    typeof PYF.getItemQuantity === "function"
      ? PYF.getItemQuantity(product.productId)
      : 0;


  /*
   * Reuse the exact same inventory, sold-out,
   * bag-capacity and state logic as the product card.
   */
  PYF.addProductFromCard(activeCard);


  const quantityAfter =
    product &&
    typeof PYF.getItemQuantity === "function"
      ? PYF.getItemQuantity(product.productId)
      : quantityBefore;


  /*
   * Close only when a pick was successfully added.
   * The popup remains open if inventory or bag limits
   * prevented the addition.
   */
  if (quantityAfter > quantityBefore) {
    PYF.closeInfoModal();
  }
};


/**
 * Connect the shared popup Add to Bag button.
 *
 * Event delegation keeps it working when Shopify
 * reloads the section inside the Theme Editor.
 */
if (
  document.documentElement.dataset
    .pyfInfoAddInitialized !== "true"
) {
  document.documentElement.dataset
    .pyfInfoAddInitialized = "true";


  document.addEventListener(
    "click",
    function (event) {
      const addButton =
        event.target.closest(
          "#pyf-info-add-button"
        );

      if (!addButton) return;


      event.preventDefault();
      event.stopPropagation();


      if (
        addButton.disabled ||
        addButton.getAttribute(
          "aria-disabled"
        ) === "true"
      ) {
        return;
      }


      PYF.addProductFromInfoModal();
    }
  );
}


/**
 * Initialize after the page loads.
 */
if (
  document.readyState === "loading"
) {
  document.addEventListener(
    "DOMContentLoaded",
    PYF.initInfoModal
  );
} else {
  PYF.initInfoModal();
}


/**
 * Reinitialize when Shopify reloads
 * the section in the Theme Editor.
 */
document.addEventListener(
  "shopify:section:load",
  PYF.initInfoModal
);